import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Fade, IconButton, Link, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Event as EventIcon, MenuBook as MenuBookIcon, PlayCircleOutline as PlayIcon } from "@mui/icons-material";

interface NoticeAlert {
    id: string;
    message: string;
    linkText: string;
    url: string;
    ariaLabel: string;
    displayDuration?: number;
    icon: React.ReactNode;
}

interface BannerPersistence {
    [bannerId: string]: {
        dismissCount: number;
        permanentlyDismissed: boolean;
        lastDismissedAt: string;
    };
}

const NOTICE_ALERTS: NoticeAlert[] = [
    {
        id: "context-lab-office-hours",
        message: "Get hands-on help in our weekly Office Hours — real questions, real answers.",
        linkText: "Reserve Your Spot →",
        url: "https://calendly.com/noel-wilson-dtsz/the-context-lab-30-minutes-to-production",
        ariaLabel: "Schedule a session for The Context Lab Office Hours",
        displayDuration: 4000,
        icon: <EventIcon sx={{ fontSize: 16 }} />
    },
    {
        id: "watch-videos",
        message: "Watch a quick demo on how to build Products and Features with Intelligenic.",
        linkText: "Watch Video →",
        url: "https://intelligenic.ai/podcasts/product-studio-demo/",
        ariaLabel: "Watch a demo of building Products and Features with Intelligenic",
        displayDuration: 4000,
        icon: <PlayIcon sx={{ fontSize: 16 }} />
    },
    {
        id: "getting-started-guide",
        message: "New to Product Studio? Start with our Getting Started Guide.",
        linkText: "Open Guide →",
        url: "https://intelligenic.ai/getting-started/",
        ariaLabel: "Read the Getting Started guide",
        displayDuration: 4000,
        icon: <MenuBookIcon sx={{ fontSize: 16 }} />
    }
];

const PERSISTENCE_KEY = "admin_banner_persistence";
const SESSION_DISMISS_KEY = "banner_dismissed_this_session";
const DISMISSAL_THRESHOLD = 2;

function getPersistence(): BannerPersistence {
    try {
        const stored = localStorage.getItem(PERSISTENCE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
}

const NoticeBar = () => {
    const [visibleAlerts, setVisibleAlerts] = useState<NoticeAlert[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const fadeDuration = prefersReducedMotion ? 0 : 3000;

    useEffect(() => {
        const sessionDismissed = sessionStorage.getItem(SESSION_DISMISS_KEY) === "true";
        if (sessionDismissed) {
            setVisibleAlerts([]);
            return;
        }
        const persistence = getPersistence();
        setVisibleAlerts(NOTICE_ALERTS.filter(alert => !persistence[alert.id]?.permanentlyDismissed));
    }, []);

    useEffect(() => {
        if (visibleAlerts.length <= 1) return;
        const duration = visibleAlerts[currentIndex]?.displayDuration || 8000;
        let swapTimeout: NodeJS.Timeout | null = null;

        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setIsTransitioning(true);
            swapTimeout = setTimeout(() => {
                setCurrentIndex(prev => (prev + 1) % visibleAlerts.length);

                // Ensure the new alert is mounted before we trigger fade-in.
                setTimeout(() => {
                    setIsTransitioning(false);
                }, 0);
            }, fadeDuration);
        }, duration);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (swapTimeout) clearTimeout(swapTimeout);
        };
    }, [visibleAlerts.length, currentIndex, fadeDuration]);

    const handleDismiss = useCallback(() => {
        const id = visibleAlerts[currentIndex]?.id;
        sessionStorage.setItem(SESSION_DISMISS_KEY, "true");
        setVisibleAlerts([]);

        if (!id) return;
        const persistence = getPersistence();
        const record = persistence[id] || { dismissCount: 0, permanentlyDismissed: false, lastDismissedAt: "" };
        record.dismissCount += 1;
        record.lastDismissedAt = new Date().toISOString();
        if (record.dismissCount >= DISMISSAL_THRESHOLD) {
            record.permanentlyDismissed = true;
        }
        persistence[id] = record;
        localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(persistence));
    }, [visibleAlerts, currentIndex]);

    const handleOpenLink = useCallback((url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    }, []);

    if (visibleAlerts.length === 0) return null;

    const current = visibleAlerts[currentIndex] || visibleAlerts[0];

    return (
        <Box
            role="region"
            aria-live="polite"
            aria-atomic="true"
            aria-label="System notifications and announcements"
            sx={{
                position: "relative",
                background: "linear-gradient(180deg, #0f172a 0%, #131a30 100%)",
                color: "#e2e8f0",
                px: { xs: 2, sm: 3 },
                py: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minHeight: 44
            }}
        >
            <Fade in={!isTransitioning} timeout={fadeDuration} easing="ease-in-out" appear={false}>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", color: "#ecf1ff" }}>{current.icon}</Box>
                    <Typography variant="body2" sx={{ fontSize: 13, color: "#cbd5e1" }} role="alert" aria-label={current.ariaLabel}>
                        {current.message}
                        <Link
                            component="button"
                            type="button"
                            onClick={() => handleOpenLink(current.url)}
                            underline="none"
                            sx={{
                                ml: 1,
                                color: "#ecf1ff",
                                fontWeight: 600,
                                fontSize: 13,
                                "&:hover": { color: "#f7f9ff" },
                                "&:focus-visible": {
                                    outline: "2px solid #ecf1ff",
                                    outlineOffset: "2px",
                                    borderRadius: "2px"
                                }
                            }}
                        >
                            {current.linkText}
                        </Link>
                    </Typography>
                </Box>
            </Fade>
            <IconButton
                size="small"
                onClick={handleDismiss}
                aria-label="Dismiss notice"
                sx={{
                    color: "#94a3b8",
                    "&:hover": { color: "#e2e8f0", backgroundColor: "rgba(255,255,255,0.08)" }
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export default NoticeBar;
